<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import {
  UiText,
  UiIcon,
  UiButton,
  UiDialog,
  UiMultiSelectChips,
  PartnerChip,
} from '@shopexperts/shared';
import type { AdminExpertDetail, PartnerBadge } from '@shopexperts/legacy-api';
import { useExpertBadgesManagement } from '@/app/composables/expert-manager/useExpertBadgesManagement';
import { useAdminBadges } from '@/app/composables/badges/useAdminBadges';

const props = defineProps<{
  expert: AdminExpertDetail;
}>();

const expertId = computed(() => props.expert.id);

const { syncBadges, isSyncing } = useExpertBadgesManagement({ expertId });

const { badges: allActiveBadges, isLoading: isLoadingAllBadges } = useAdminBadges({
  status: 'active',
  limit: 100,
});

const draftBadges = ref<PartnerBadge[]>([...props.expert.badges]);

watch(
  () => props.expert.badges,
  (next) => {
    draftBadges.value = [...next];
  },
);

const draftIds = computed(() => draftBadges.value.map((b) => b.id));
const savedIds = computed(() => props.expert.badges.map((b) => b.id));

const isDirty = computed(() => {
  if (draftIds.value.length !== savedIds.value.length) return true;
  const draftSet = new Set(draftIds.value);
  return savedIds.value.some((id) => !draftSet.has(id));
});

const pendingAddCount = computed(() => {
  const savedSet = new Set(savedIds.value);
  return draftIds.value.filter((id) => !savedSet.has(id)).length;
});

const pendingRemoveCount = computed(() => {
  const draftSet = new Set(draftIds.value);
  return savedIds.value.filter((id) => !draftSet.has(id)).length;
});

const addDialogOpen = ref(false);
const selectedBadgeIds = ref<string[]>([]);

const draftIdSet = computed(() => new Set(draftIds.value));

const availableBadgeOptions = computed(() =>
  allActiveBadges.value
    .filter((badge) => !draftIdSet.value.has(badge.id))
    .map((badge) => ({
      label: badge.name,
      value: String(badge.id),
    })),
);

watch(addDialogOpen, (open) => {
  if (open) selectedBadgeIds.value = [];
});

function getBadgeIconUrl(badge: { icon?: { cdn_url?: string; url?: string } | null }): string | undefined {
  return badge.icon?.cdn_url || badge.icon?.url || undefined;
}

function isPendingAdd(badgeId: number) {
  return !savedIds.value.includes(badgeId);
}

function removeFromDraft(badgeId: number) {
  draftBadges.value = draftBadges.value.filter((b) => b.id !== badgeId);
}

function onConfirmAdd() {
  if (selectedBadgeIds.value.length === 0) {
    addDialogOpen.value = false;
    return;
  }
  const newIds = selectedBadgeIds.value.map((v) => Number(v));
  const additions = allActiveBadges.value.filter((badge) => newIds.includes(badge.id));
  draftBadges.value = [...draftBadges.value, ...additions];
  addDialogOpen.value = false;
}

function discardChanges() {
  draftBadges.value = [...props.expert.badges];
}

async function saveChanges() {
  await syncBadges.mutateAsync(draftIds.value);
}
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-start justify-between gap-3">
      <div class="min-w-0">
        <UiText as="h6" variant="h6" class="font-semibold text-se-dark">App & Theme Skills</UiText>
        <UiText as="p" variant="p-small" class="text-gray-500 mt-1">
          Manage the badges assigned to this expert.
        </UiText>
      </div>
      <UiButton
        variant="outline"
        label="Add Badges"
        :disabled="isSyncing"
        @click="addDialogOpen = true"
      >
        <template #before_label>
          <UiIcon name="plus-square" class="w-4 h-4" />
        </template>
      </UiButton>
    </div>

    <div v-if="draftBadges.length > 0" class="flex flex-wrap gap-2">
      <PartnerChip
        v-for="badge in draftBadges"
        :key="badge.id"
        :name="badge.name"
        :icon-url="getBadgeIconUrl(badge)"
        size="sm"
        rounded="small"
        :class="isPendingAdd(badge.id) ? 'ring-1 ring-emerald-300 rounded-md' : ''"
      >
        <template #inline-after>
          <UiButton
            variant="ghost"
            shape="icon"
            :disabled="isSyncing"
            class="hover:bg-se-danger/10 rounded-full transition-colors disabled:opacity-50 translate-x-1/4"
            :title="`Remove ${badge.name}`"
            @click.stop="removeFromDraft(badge.id)"
          >
            <template #before_label>
              <UiIcon name="close" class="w-4 h-4 text-se-dark" />
            </template>
          </UiButton>
        </template>
      </PartnerChip>
    </div>

    <div v-else class="py-10 text-center border border-dashed border-gray-200 rounded-lg">
      <UiIcon name="image-placeholder" class="w-8 h-8 text-gray-300 mx-auto mb-2" />
      <UiText as="p" variant="p-small" class="text-gray-400">No badges assigned</UiText>
    </div>

    <div
      v-if="isDirty"
      class="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <UiText as="p" variant="p-small" class="text-amber-800">
        Unsaved changes:
        <span v-if="pendingAddCount" class="font-medium">{{ pendingAddCount }} to add</span>
        <span v-if="pendingAddCount && pendingRemoveCount">, </span>
        <span v-if="pendingRemoveCount" class="font-medium">{{ pendingRemoveCount }} to remove</span>
      </UiText>
      <div class="flex items-center gap-2">
        <UiButton
          variant="outline"
          label="Discard"
          :disabled="isSyncing"
          @click="discardChanges"
        />
        <UiButton
          variant="primary"
          label="Save Changes"
          :disabled="isSyncing"
          @click="saveChanges"
        />
      </div>
    </div>

    <UiDialog
      v-model="addDialogOpen"
      title="Add Badges"
      text="Select one or more badges. They'll be staged until you save."
      size="lg"
      primary-action-label="Add to Draft"
      secondary-action-label="Cancel"
      @primary-action="onConfirmAdd"
      @secondary-action="addDialogOpen = false"
    >
      <div class="flex flex-col gap-3">
        <UiText
          v-if="!isLoadingAllBadges && availableBadgeOptions.length === 0"
          as="p"
          variant="p-small"
          class="text-gray-500"
        >
          No more badges available to assign.
        </UiText>

        <UiMultiSelectChips
          v-else
          v-model="selectedBadgeIds"
          :options="availableBadgeOptions"
          placeholder="Search and select badges..."
          open-on-click
          show-chevron
        />
      </div>
    </UiDialog>
  </div>
</template>
